/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import * as z from "zod";
import type { ToZod } from "../.kubb/ToZod.ts";
import type { CourseDetailViewModel } from "../types/CourseDetailViewModel.ts";
import { courseCertificateRelatedViewModelSchema } from "./courseCertificateRelatedViewModelSchema.ts";
import { eCourseAreaSchema } from "./eCourseAreaSchema.ts";
import { eCourseHistoryTypeSchema } from "./eCourseHistoryTypeSchema.ts";
import { eCourseModalitySchema } from "./eCourseModalitySchema.ts";
import { eCourseOnDemandProgressionTypeSchema } from "./eCourseOnDemandProgressionTypeSchema.ts";
import { eCourseStatusSchema } from "./eCourseStatusSchema.ts";
import { eRegistrationFormTypeSchema } from "./eRegistrationFormTypeSchema.ts";
import { eTypeCourseSchema } from "./eTypeCourseSchema.ts";

export const courseDetailViewModelSchema = z.object({
  id: z.optional(z.number().int().describe("ID do curso.")),
  code: z.string().describe("Código do curso, único por empresa.").nullish(),
  codeOld: z
    .string()
    .describe(
      "Código anterior do curso, mantido para rastreabilidade de migrações. Opcional.",
    )
    .nullish(),
  codePayment: z
    .string()
    .describe("Código de integração usado na mensalidade. Opcional.")
    .nullish(),
  codeRegistration: z
    .string()
    .describe("Código de integração usado na inscrição. Opcional.")
    .nullish(),
  status: z.optional(
    z
      .lazy(() => eCourseStatusSchema)
      .describe("1 = Released | 2 = Canceled | 3 = Finalized | "),
  ),
  modality: z.optional(
    z
      .lazy(() => eCourseModalitySchema)
      .describe(
        "1 = Distance | 2 = Presential | 3 = SemiClassroom | 4 = OnDemand | ",
      ),
  ),
  typeCourse: z.optional(
    z
      .lazy(() => eTypeCourseSchema)
      .describe(
        "1 = PostGraduation | 2 = Improvement | 3 = Update | 4 = Diffusion | 5 = FreeCourse | 6 = Event | 7 = MbaUpdate | 8 = Graduation | 9 = Workshop | 10 = Lecture | ",
      ),
  ),
  courseArea: z.optional(
    z
      .lazy(() => eCourseAreaSchema)
      .describe(
        "1 = Exact | 2 = Biological | 3 = Human | 4 = VeterinaryMedicine | ",
      ),
  ),
  registrationFormType: z.optional(
    z
      .lazy(() => eRegistrationFormTypeSchema)
      .describe(
        "1 = Complete | 2 = Compact | 3 = CompactWithVerification | 4 = Simple | 5 = Latest | 6 = LatestFMVZ | 7 = LatestPecegeCollege | 8 = LatestPoli | 9 = LatestCiaam | 10 = LatestPecegeCollegeMBA | 11 = LatestMiniCourse | ",
      ),
  ),
  historyType: z.optional(
    z
      .lazy(() => eCourseHistoryTypeSchema)
      .describe("1 = ByDiscipline | 2 = ByClassGroup | "),
  ),
  onDemandProgressionType: z.optional(
    z
      .lazy(() => eCourseOnDemandProgressionTypeSchema)
      .describe(
        "1 = None | 2 = Ordered | 3 = ClassDuration | 4 = Limited | 5 = Grade | ",
      ),
  ),
  onDemandProgressionLimitDays: z
    .number()
    .int()
    .describe(
      "Limite de dias da progressão OnDemand. Preenchido apenas quando a progressão é Limited. Opcional.",
    )
    .nullish(),
  onDemandProgressionLimitClasses: z
    .number()
    .int()
    .describe(
      "Limite de aulas da progressão OnDemand. Preenchido apenas quando a progressão é Limited. Opcional.",
    )
    .nullish(),
  onDemandProgressionExam: z.optional(
    z
      .boolean()
      .describe(
        "Indica se a avaliação é exigida para progressão. Sempre verdadeiro quando a progressão é por nota.",
      ),
  ),
  duration: z
    .number()
    .int()
    .describe("Duração do curso em dias. Opcional.")
    .nullish(),
  minGrade: z
    .number()
    .describe("Nota mínima para aprovação, entre 0 e 10. Opcional.")
    .nullish(),
  maxGrade: z
    .number()
    .describe(
      "Nota máxima do curso, entre 0 e 10. Não pode ser menor que a nota mínima. Opcional.",
    )
    .nullish(),
  gradePointAverage: z
    .number()
    .describe(
      "Média exigida para emissão automática do certificado de conclusão, entre 0 e 10. Opcional.",
    )
    .nullish(),
  companyId: z
    .number()
    .int()
    .describe(
      "ID da empresa dona do curso. Nulo quando o curso é da matriz. Não pode ser alterado após a criação. Opcional.",
    )
    .nullish(),
  companyName: z
    .string()
    .describe(
      "Nome da empresa dona do curso. Vazio quando o curso é da matriz.",
    )
    .nullish(),
  courseGroupId: z
    .number()
    .int()
    .describe("ID do grupo de curso associado. Opcional.")
    .nullish(),
  suggestedMonitorId: z
    .number()
    .int()
    .describe("ID da pessoa sugerida como monitor do curso. Opcional.")
    .nullish(),
  ssoCategoryId: z
    .number()
    .int()
    .describe("ID da categoria de SSO usada na integração. Opcional.")
    .nullish(),
  googleTagManagerIdentifier: z
    .string()
    .describe(
      "Identificador do Google Tag Manager do curso, no padrão GTM-XXXXXX. Opcional.",
    )
    .nullish(),
  thumbnail: z
    .string()
    .describe("Nome do arquivo de miniatura do curso no storage. Opcional.")
    .nullish(),
  active: z.optional(z.boolean().describe("Indica se o curso está ativo.")),
  inactiveDate: z
    .string()
    .datetime()
    .describe("Data em que o curso foi inativado. Opcional.")
    .nullish(),
  showInLogin: z.optional(
    z.boolean().describe("Indica se o curso é destacado na tela de login."),
  ),
  openCourse: z.optional(z.boolean().describe("Indica se o curso é aberto.")),
  requiredApproval: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso exige aprovação de documentação. Só pode ser habilitado quando todos os planos financeiros possuem valor de inscrição.",
      ),
  ),
  automaticApproval: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso possui aprovação automática de matrícula. Aplicável apenas a curso da matriz.",
      ),
  ),
  automaticApprovalForCompany: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso possui aprovação automática para a empresa. Aplicável apenas a curso de empresa.",
      ),
  ),
  representativeRequired: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso exige representante da empresa. Aplicável apenas a curso de empresa.",
      ),
  ),
  hasMonograph: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso possui monografia. Sempre falso em curso de empresa.",
      ),
  ),
  internationalCourse: z.optional(
    z.boolean().describe("Indica se o curso é internacional."),
  ),
  accessHelpcenter: z.optional(
    z.boolean().describe("Indica se o curso libera acesso à central de ajuda."),
  ),
  requirePostGraduateDocument: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso exige diploma de pós-graduação na matrícula.",
      ),
  ),
  requireUniversityDegree: z.optional(
    z
      .boolean()
      .describe("Indica se o curso exige diploma de graduação na matrícula."),
  ),
  requireCodeUsp: z.optional(
    z.boolean().describe("Indica se o curso exige o código USP do aluno."),
  ),
  mobileAccess: z.optional(
    z.boolean().describe("Indica se o curso permite acesso pelo aplicativo."),
  ),
  onlyAccessActive: z.optional(
    z
      .boolean()
      .describe("Indica se apenas matrículas ativas acessam o conteúdo."),
  ),
  validateContract: z.optional(
    z
      .boolean()
      .describe("Indica se o contrato do curso exige validação do aluno."),
  ),
  automaticCompletionCertificate: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o certificado de conclusão é emitido automaticamente. Quando verdadeiro, a média para aprovação é obrigatória.",
      ),
  ),
  hasSelectiveProcess: z.optional(
    z.boolean().describe("Indica se o curso possui processo seletivo."),
  ),
  showCommunicationLanguageCompactReg: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o idioma de comunicação é exibido na inscrição compacta.",
      ),
  ),
  releasePaymentSelection: z.optional(
    z
      .boolean()
      .describe(
        "Indica se a seleção de pagamento libera o acesso à sala de aula.",
      ),
  ),
  releaseEnrollmentBatchStatusChange: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o curso permite alteração de status de matrícula em lote.",
      ),
  ),
  spReleasedUntil: z
    .string()
    .datetime()
    .describe(
      "Data limite de liberação de acesso para matrículas com seleção de pagamento. Opcional.",
    )
    .nullish(),
  ppAndPAReleasedUntil: z
    .string()
    .datetime()
    .describe(
      "Data limite de liberação de acesso para matrículas com pagamento pendente e aprovação pendente. Opcional.",
    )
    .nullish(),
  endActionWithAP: z
    .string()
    .datetime()
    .describe(
      "Data de encerramento da ação com aprovação pendente, usada na integração. Opcional.",
    )
    .nullish(),
  cultureInfo: z
    .string()
    .describe(
      "Idioma do conteúdo retornado, resolvido pelo idioma corrente com fallback para o idioma padrão.",
    )
    .nullish(),
  nameOfficial: z
    .string()
    .describe("Nome oficial do curso no idioma retornado.")
    .nullish(),
  nameCommercial: z
    .string()
    .describe("Nome comercial do curso no idioma retornado.")
    .nullish(),
  description: z
    .string()
    .describe("Descrição do curso no idioma retornado. Opcional.")
    .nullish(),
  contentHtml: z
    .string()
    .describe("Regulamento do curso em HTML, no idioma retornado. Opcional.")
    .nullish(),
  contentInstruction: z
    .string()
    .describe("Instruções do curso no idioma retornado. Opcional.")
    .nullish(),
  useMoveTCC: z.optional(
    z
      .boolean()
      .describe(
        "Indica se alguma turma do curso utiliza o Move TCC. Quando verdadeiro, a monografia interna não pode ser habilitada.",
      ),
  ),
  allowMonograph: z.optional(
    z
      .boolean()
      .describe(
        "Indica se a monografia pode ser habilitada: verdadeiro somente para curso da matriz sem turma usando Move TCC.",
      ),
  ),
  allowAutomaticApproval: z.optional(
    z
      .boolean()
      .describe(
        "Indica se a aprovação automática se aplica ao curso: verdadeiro somente para curso da matriz.",
      ),
  ),
  allowCompanyApprovalFields: z.optional(
    z
      .boolean()
      .describe(
        "Indica se os campos de aprovação para empresa e representante obrigatório se aplicam: verdadeiro somente para curso de empresa.",
      ),
  ),
  allowOnDemandFields: z.optional(
    z
      .boolean()
      .describe(
        "Indica se os campos de progressão OnDemand se aplicam: verdadeiro somente na modalidade OnDemand.",
      ),
  ),
  allowHistoryTypeSelection: z.optional(
    z
      .boolean()
      .describe(
        "Indica se o tipo de histórico é configurável: verdadeiro somente na modalidade OnDemand.",
      ),
  ),
  lastChangeDate: z
    .string()
    .datetime()
    .describe("Data da última alteração. Opcional.")
    .nullish(),
  userLastChange: z
    .string()
    .describe("Usuário responsável pela última alteração. Opcional.")
    .nullish(),
  certificates: z
    .array(z.lazy(() => courseCertificateRelatedViewModelSchema))
    .describe("Certificados relacionados ao curso.")
    .nullish(),
}) as unknown as ToZod<CourseDetailViewModel>;
